import { computed } from '@angular/core';
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import { Event } from 'api/models';

export interface EventWithOrgSiteKey extends Event {
    read_only: boolean;
    orgSiteKey: string;
}

export interface EventDataChangeInfo {
    type: 'add' | 'update' | 'delete' | 'refresh' | 'load';
    data?: EventWithOrgSiteKey;
    timestamp: number;
}

export interface EventsState {
    events: EventWithOrgSiteKey[];
    loading: boolean;
    error: string | null;
    lastUpdated: number;
    totalCount: number;
    dataChange: EventDataChangeInfo;
    newNotifications: boolean;
}

export const initialEventsState: EventsState = {
    events: [],
    loading: false,
    error: null,
    lastUpdated: 0,
    totalCount: 0,
    dataChange: {
        type: 'refresh',
        data: undefined,
        timestamp: 0,
    },
    newNotifications: false,
};

export const EventsStore = signalStore(
    { providedIn: 'root' },
    withState<EventsState>(initialEventsState),
    withComputed(({ events }) => ({
        filteredEvents: computed(() => events()),
    })),
    withMethods(store => {
        const triggerEventsDataChange = (
            type: EventDataChangeInfo['type'],
            data?: EventWithOrgSiteKey
        ) => {
            patchState(store, {
                dataChange: { type, data, timestamp: Date.now() },
            });
        };

        return {
            setEventsLoading(loading: boolean): void {
                patchState(store, { loading });
            },
            setEventsError(error: string | null): void {
                patchState(store, { error });
            },
            setEvents(events: EventWithOrgSiteKey[]): void {
                patchState(store, {
                    events,
                    totalCount: events.length,
                    lastUpdated: Date.now(),
                });
                triggerEventsDataChange('load');
            },
            addEvent(event: EventWithOrgSiteKey): void {
                patchState(store, state => ({
                    events: [...state.events, event],
                    totalCount: state.totalCount + 1,
                    lastUpdated: Date.now(),
                }));
                triggerEventsDataChange('add', event);
            },
            updateEvent(updatedEvent: EventWithOrgSiteKey): void {
                patchState(store, state => ({
                    events: state.events.map(event =>
                        event.eventKey === updatedEvent.eventKey
                            ? updatedEvent
                            : event
                    ),
                    lastUpdated: Date.now(),
                }));
                triggerEventsDataChange('update', updatedEvent);
            },
            removeEvent(eventKey: string): void {
                const eventToRemove = store.events().find(
                    event => event.eventKey === eventKey
                );
                patchState(store, state => ({
                    events: state.events.filter(
                        event => event.eventKey !== eventKey
                    ),
                    totalCount: Math.max(0, state.totalCount - 1),
                    lastUpdated: Date.now(),
                }));
                if (eventToRemove) {
                    triggerEventsDataChange('delete', eventToRemove);
                }
            },
            clearEvents(): void {
                patchState(store, {
                    events: [],
                    totalCount: 0,
                    lastUpdated: Date.now(),
                });
                triggerEventsDataChange('refresh');
            },
            refreshEvents(): void {
                triggerEventsDataChange('refresh');
            },
            triggerEventsDataChange,
            bulkAddEvents(newEvents: EventWithOrgSiteKey[]): void {
                patchState(store, state => ({
                    events: [...newEvents, ...state.events],
                    totalCount: state.totalCount + newEvents.length,
                    lastUpdated: Date.now(),
                }));
                triggerEventsDataChange('refresh');
            },
            setNewNotifications(value: boolean): void {
                patchState(store, { newNotifications: value });
            },
        };
    })
);

